import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { Button } from '@app/components/ui/button';
import { getPostHogClient } from '@cloud-ui/components/posthog/posthogClient';
import { ROUTES } from '@cloud-ui/constants';
import { usePylonDraggable } from '@cloud-ui/hooks/usePylonDraggable';
import { cleanupPylon, loadPylonScript } from '@cloud-ui/services/pylon';
import { callApi } from '@cloud-ui/utils/api';
import {
  clearSessionAuthStorage,
  ORGANIZATION_SESSION_MUTATION_KEY,
  subscribeToSessionChanges,
  subscribeToSessionReview,
  trackAuthSession,
} from '@cloud-ui/utils/authSessionStorage';
import { discardCapturedInvite } from '@cloud-ui/utils/pendingInvite';
import type { OrganizationDTO } from '@shared/dto/organizations';
import type { UserDTO } from '@shared/dto/users';

import { Navigate, useLocation } from 'react-router-dom';

import { useConfig } from './ConfigContext';

interface LogoutCompletion {
  /** Restore verified storage and queue navigation, without repeating server logout. */
  run: () => undefined | false;
  /** Discard only browser state still owned by this handoff. */
  discard: () => void;
}

interface AuthContextType {
  user: UserDTO | null;
  organization: OrganizationDTO | null;
  login: () => Promise<void>;
  logout: (options?: {
    preserveInvitation?: boolean;
    completion?: LogoutCompletion;
  }) => Promise<void>;
  /** Retry a failed post-logout handoff without ending another session. */
  retryLogoutCompletion: () => void;
  /** Queue a full refresh after session writes; retain optional handoff cleanup for later logout. */
  reloadSession: (
    destination: string,
    options?: { replace?: boolean; onSessionEnd?: () => void },
  ) => void;
  isLoading: boolean;
  isAdmin: boolean;
}

// Background 401 handlers can ignore failures; interactive logout must report them.
export const logout = async ({ throwOnError = false } = {}) => {
  try {
    // Call the logout endpoint to destroy the session
    await callApi('/auth/logout', { method: 'POST' }, { skip401Redirect: true });
  } catch (error) {
    console.error('Error during logout:', error);
    if (throwOnError) {
      throw error;
    }
  }
};

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserDTO | null>(null);
  const [organization, setOrganization] = useState<OrganizationDTO | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [needsSessionReview, setNeedsSessionReview] = useState(false);
  const sessionReviewVersion = useRef(0);
  const [pylonEmailHash, setPylonEmailHash] = useState<string | undefined>(undefined);
  const sessionGeneration = useRef(0);
  const pendingNavigation = useRef<{
    destination: string;
    replace: boolean;
    attempted?: boolean;
  } | null>(null);
  // A canceled beforeunload has no completion signal. Keep the old UI unmounted until the
  // document leaves, including its portals, while already-started session writes settle.
  const refreshReason = useRef<'local' | 'organization' | 'logout' | null>(null);
  const [refresh, setRefresh] = useState<{
    reason: 'local' | 'organization' | 'logout';
    waiting: boolean;
  } | null>(null);
  const pendingLogouts = useRef(0);
  const isMounted = useRef(true);
  const pendingLogoutCompletion = useRef<{
    completion: LogoutCompletion;
    session: ReturnType<typeof trackAuthSession> | null;
  } | null>(null);
  const [completionError, setCompletionError] = useState<string | null>(null);
  // The caller unmounts at the refresh boundary. Keep its owned handoff cleanup independent
  // of the destination, which a later peer organization selection can replace.
  const navigationSessionEnd = useRef<(() => void) | null>(null);
  // The storage listener is registered once and would only ever see the first render's state.
  const signedInUser = useRef(user);
  signedInUser.current = user;
  const isFindingOutWhoIsSignedIn = useRef(isLoading);
  isFindingOutWhoIsSignedIn.current = isLoading;
  const queryClient = useQueryClient();
  const { browserTelemetryReady, disableTelemetry, isPromptfooCloud } = useConfig();

  const clearIdentity = () => {
    setUser(null);
    setOrganization(null);
    setIsAdmin(false);
    setPylonEmailHash(undefined);
  };

  const fetchUserData = async () => {
    const requestGeneration = sessionGeneration.current;
    const reviewVersion = sessionReviewVersion.current;
    try {
      // For session-based auth, we don't need to check for tokens
      // The session cookie will be sent automatically
      const response = await callApi('/users/me', {}, { skip401Redirect: true });
      const data = response.ok ? await response.json() : null;
      if (requestGeneration !== sessionGeneration.current) {
        return;
      }

      if (response.ok) {
        // An older identity read must not dismiss a recovery request received while it waited.
        if (reviewVersion === sessionReviewVersion.current) {
          setNeedsSessionReview(false);
        }
        setUser(data.user);
        setPylonEmailHash(data.pylonEmailHash);
        if (data.organization) {
          setOrganization(data.organization);
          setIsAdmin(data.isAdmin);
        }
      } else {
        if (response.status === 401) {
          console.debug('User not authenticated, clearing auth state');
        } else {
          console.error(`Unexpected response status: ${response.status}`);
        }
        clearIdentity();
      }
    } catch (error) {
      if (requestGeneration !== sessionGeneration.current) {
        return;
      }
      console.error('Failed to fetch user data:', error);
      // Clear state on error - fail closed
      clearIdentity();
    } finally {
      if (requestGeneration === sessionGeneration.current) {
        setIsLoading(false);
      }
    }
  };

  const login = async () => {
    await fetchUserData();
  };

  const discardLogoutCompletion = ({ discardState = true } = {}) => {
    const current = pendingLogoutCompletion.current;
    pendingLogoutCompletion.current = null;
    current?.session?.dispose();
    if (discardState) {
      current?.completion.discard();
    }
  };

  const clearAccountData = ({ keepRefreshBoundary = false, keepLogoutCompletion = false } = {}) => {
    setNeedsSessionReview(false);
    const endNavigationSession = navigationSessionEnd.current;
    navigationSessionEnd.current = null;
    endNavigationSession?.();
    if (!keepLogoutCompletion) {
      discardLogoutCompletion();
      setCompletionError(null);
    }
    pendingNavigation.current = null;
    refreshReason.current = keepRefreshBoundary ? 'logout' : null;
    if (!keepRefreshBoundary) {
      setRefresh(null);
    }
    sessionGeneration.current += 1;
    clearIdentity();
    setIsLoading(false);

    // Removing account queries also cancels in-flight fetches. Keep public auth
    // configuration available so the sign-in screen can render immediately.
    queryClient.removeQueries({
      predicate: ({ queryKey }) => queryKey[0] !== 'config' && queryKey[0] !== 'fusionauth',
    });
    cleanupPylon();
  };

  const reloadAfterSessionWrites = () => {
    if (!isMounted.current) {
      return;
    }
    const waiting =
      pendingLogouts.current > 0 ||
      queryClient.isMutating({ mutationKey: ORGANIZATION_SESSION_MUTATION_KEY }) > 0;
    const reason = refreshReason.current;
    if (reason) {
      setRefresh((current) =>
        current?.reason === reason && current.waiting === waiting ? current : { reason, waiting },
      );
    }
    const navigation = pendingNavigation.current;
    if (!waiting && navigation && !navigation.attempted) {
      // Do not retry automatically on cache notifications when navigation was canceled.
      if (reason) {
        navigation.attempted = true;
      } else {
        pendingNavigation.current = null;
      }
      if (navigation.replace) {
        window.location.replace(navigation.destination);
      } else {
        window.location.assign(navigation.destination);
      }
    }
  };

  const reloadSession = (
    destination: string,
    { replace = false, onSessionEnd }: { replace?: boolean; onSessionEnd?: () => void } = {},
  ) => {
    if (!isMounted.current) {
      return;
    }
    setNeedsSessionReview(false);
    if (onSessionEnd) {
      navigationSessionEnd.current = onSessionEnd;
    }
    // A late local response cannot establish which tab committed last. Re-enter through
    // home whenever a peer selection is outstanding, rather than assume its eval is current.
    if (refreshReason.current === 'organization') {
      pendingNavigation.current ??= { destination: ROUTES.root, replace: false };
    } else {
      // Local session changes also invalidate the mounted account UI. Preserve the requested
      // destination and a provider-owned retry if the full navigation is canceled.
      refreshReason.current ??= 'local';
      pendingNavigation.current = { destination, replace };
    }
    reloadAfterSessionWrites();
  };

  const retryLogoutCompletion = () => {
    const current = pendingLogoutCompletion.current;
    if (!current?.session) {
      return;
    }
    if (!current.session.isCurrent()) {
      discardLogoutCompletion();
      return;
    }
    try {
      if (current.completion.run() === false) {
        // Superseded storage is terminal. Preserve the replacement and offer neutral sign-in,
        // rather than retry the obsolete restoration forever.
        discardLogoutCompletion();
        pendingNavigation.current = null;
        setCompletionError(null);
        return;
      }
      // Successful completion is consumed; a canceled navigation retains only its destination.
      pendingLogoutCompletion.current = null;
      current.session.dispose();
      setCompletionError(null);
    } catch (error) {
      setCompletionError(
        error instanceof Error ? error.message : 'Unable to finish signing out. Try again.',
      );
      throw error;
    }
  };

  const retryRefresh = () => {
    // Recheck live counts: a new mutation can start between rendering and this click.
    if (
      pendingLogouts.current > 0 ||
      queryClient.isMutating({ mutationKey: ORGANIZATION_SESSION_MUTATION_KEY }) > 0
    ) {
      reloadAfterSessionWrites();
      return;
    }
    if (pendingLogoutCompletion.current?.session) {
      try {
        retryLogoutCompletion();
      } catch {
        // The retained recovery screen reports the storage error and offers the same retry.
      }
      return;
    }
    pendingNavigation.current ??= { destination: ROUTES.login, replace: false };
    pendingNavigation.current.attempted = false;
    reloadAfterSessionWrites();
  };

  const logoutAndClearData = async ({
    preserveInvitation = false,
    completion,
  }: {
    preserveInvitation?: boolean;
    completion?: LogoutCompletion;
  } = {}) => {
    discardLogoutCompletion();
    setCompletionError(null);
    const requestGeneration = sessionGeneration.current;
    const current = completion
      ? { completion, session: null as ReturnType<typeof trackAuthSession> | null }
      : null;
    pendingLogoutCompletion.current = current;
    pendingLogouts.current += 1;
    try {
      await logout({ throwOnError: true });
      if (!isMounted.current || requestGeneration !== sessionGeneration.current) {
        return;
      }
      const ownsCompletion =
        isMounted.current &&
        requestGeneration === sessionGeneration.current &&
        pendingLogoutCompletion.current === current;
      // Preserve the stable recovery screen through the caller's post-logout handoff. Its
      // component may already be unmounted, but the registered completion still owns storage.
      clearAccountData({
        keepRefreshBoundary: refreshReason.current !== null,
        keepLogoutCompletion: ownsCompletion,
      });
      clearSessionAuthStorage({ preserveInvitation });
      if (current && ownsCompletion) {
        current.session = trackAuthSession();
        retryLogoutCompletion();
      }
    } catch (error) {
      // A failed server logout must not retain a completion that has never owned a signed-out
      // session. A failed storage completion remains retryable without another logout request.
      if (current && pendingLogoutCompletion.current === current && !current.session) {
        discardLogoutCompletion({ discardState: false });
      }
      throw error;
    } finally {
      pendingLogouts.current -= 1;
      reloadAfterSessionWrites();
    }
  };

  useEffect(() => {
    isMounted.current = true;
    fetchUserData();

    // Mutation success callbacks publish this tab's writes before the pending count drops.
    // Reloading earlier could unload a request whose later commit would never notify peers.
    const unsubscribeMutations = queryClient.getMutationCache().subscribe(reloadAfterSessionWrites);
    const unsubscribeReview = subscribeToSessionReview(() => {
      sessionReviewVersion.current += 1;
      setNeedsSessionReview(true);
    });
    const unsubscribeSession = subscribeToSessionChanges((type) => {
      if (type === 'auth_logout_event') {
        pendingNavigation.current = null;
        // Another tab logged out, sync this tab
        console.debug('Logout detected in another tab, syncing state');
        // An invite this tab holds was being asked of the user who has just been signed out, so
        // it ends with their session. So does one in a tab that is still finding out who is
        // signed in: the logout says that someone was. A tab that shows the landing page to a
        // signed-out visitor has asked no one, and keeps its code.
        if (signedInUser.current || isFindingOutWhoIsSignedIn.current) {
          discardCapturedInvite();
        }
        clearAccountData({ keepRefreshBoundary: refreshReason.current !== null });
        reloadAfterSessionWrites();
        // Note: Session cookie is cleared server-side (httpOnly flag prevents client-side removal)
      }
      if (type === 'auth_organization_event') {
        // Another tab moved the session into another organization. What this tab shows belongs
        // to the one before, and what it sends next would act on the new one. So it starts
        // over, at the page that a switch in this tab leads to.
        if (refreshReason.current !== 'organization') {
          pendingNavigation.current = { destination: ROUTES.root, replace: false };
        }
        refreshReason.current = 'organization';
        reloadAfterSessionWrites();
      }
    });
    return () => {
      isMounted.current = false;
      pendingNavigation.current = null;
      refreshReason.current = null;
      // Document/provider unmount is not a session end; preserve the outgoing handoff.
      navigationSessionEnd.current = null;
      discardLogoutCompletion({ discardState: false });
      unsubscribeReview();
      unsubscribeMutations();
      unsubscribeSession();
    };
  }, []);

  /**
   * Once the user and org are loaded, if we are running on Promptfoo Cloud,
   * (i.e. not on-prem) initialize Pylon.
   */
  useEffect(() => {
    if (user && organization && isPromptfooCloud) {
      loadPylonScript(user.name, user.email, organization.id, pylonEmailHash);
    }
  }, [user, organization, isPromptfooCloud, pylonEmailHash]);

  useEffect(() => {
    if (!browserTelemetryReady || disableTelemetry || !user || !organization) {
      return;
    }

    const posthogClient = getPostHogClient();
    if (!posthogClient) {
      return;
    }

    posthogClient.identify(user.id, {
      organizationId: organization.id,
      organization: organization.name,
    });
    posthogClient.group('organization', organization.id, {
      name: organization.name,
    });
  }, [browserTelemetryReady, disableTelemetry, user, organization]);

  // Make Pylon chat bubble draggable so users can reposition it
  usePylonDraggable();

  return (
    <AuthContext.Provider
      value={{
        isAdmin,
        user,
        organization,
        login,
        logout: logoutAndClearData,
        retryLogoutCompletion,
        reloadSession,
        isLoading,
      }}
    >
      {refresh ? (
        <main className="flex min-h-screen items-center justify-center p-6">
          <section className="max-w-md space-y-4" aria-live="polite">
            <h1 className="text-xl font-semibold">
              {refresh.reason === 'organization'
                ? 'Organization changed in another tab'
                : refresh.reason === 'logout'
                  ? 'You are signed out'
                  : 'Refresh your session'}
            </h1>
            <p>
              {refresh.waiting
                ? 'Finishing your current session changes before continuing.'
                : refresh.reason === 'organization'
                  ? 'Refresh to load your current organization before continuing.'
                  : refresh.reason === 'logout'
                    ? 'Continue to finish signing out or switching accounts.'
                    : 'Continue to load your updated session.'}
            </p>
            {completionError && <p role="alert">{completionError}</p>}
            <Button onClick={retryRefresh} disabled={refresh.waiting}>
              {refresh.reason === 'organization' ? 'Refresh' : 'Continue'}
            </Button>
          </section>
        </main>
      ) : (
        <>
          {needsSessionReview && (
            <aside role="alert" className="border-b bg-background p-4">
              <p>
                Your session may have expired. Review it to sign in again or continue with your
                current account.
              </p>
              <Button
                onClick={() =>
                  reloadSession(
                    `${ROUTES.login}?reason=unauthorized&redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`,
                  )
                }
              >
                Review session
              </Button>
            </aside>
          )}
          {children}
        </>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading, organization } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <div></div>;
  }

  if (!user) {
    return <Navigate to={ROUTES.login} state={{ from: location }} replace />;
  }

  if (!organization) {
    return <Navigate to={ROUTES.organization.new} state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export const RequireRedteamAccess = ({ children }: { children: React.ReactNode }) => {
  const { organization, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <div></div>;
  }

  if (!organization || !organization.canUseRedteam) {
    return <Navigate to={ROUTES.upgrade} state={{ from: location }} replace />;
  }

  return <>{children}</>;
};
